// SearchBar (mobile) — backed by our unified /api/search (Mongo on Railway).
// No more WP / Tutor / Woo dependencies.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, FileText, ShoppingBag, GraduationCap } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const ICON_BY_KIND = { shop: ShoppingBag, academy: GraduationCap, blog: FileText };

function highlight(text, q) {
  if (!q) return text;
  const i = (text || "").toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]">{text.slice(i, i + q.length)}</span>
      {text.slice(i + q.length)}
    </>
  );
}

export const SearchBar = ({ className = "", size = "md", testIdPrefix = "search" }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const t = useT();
  const lang = useLang();

  // Debounced unified search
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const h = setTimeout(async () => {
      const { ok, data } = await apiFetch(`/api/search?q=${encodeURIComponent(q.trim())}&limit=10&language=${lang}`);
      setResults(ok ? (data?.items || []) : []);
    }, 220);
    return () => clearTimeout(h);
  }, [q, lang]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const groups = useMemo(() => {
    const out = { shop: [], academy: [], blog: [] };
    results.forEach((it) => { if (out[it.kind]) out[it.kind].push(it); });
    return out;
  }, [results]);

  const inputSize = size === "lg" ? "h-12 text-[15px]" : "h-10 text-[14px]";

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form onSubmit={submit} className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/45 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("nav.search_placeholder")}
          className={`w-full ${inputSize} pl-9 pr-4 rounded-[5px] bg-[color:var(--gv-mist)] border border-transparent focus:border-[color:var(--gv-pink)] focus:bg-white transition-all outline-none`}
          data-testid={`${testIdPrefix}-input`}
        />
      </form>

      {open && q.trim().length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-[5px] border border-[color:var(--gv-line)] shadow-[0_24px_60px_-20px_rgba(18,24,32,0.25)] overflow-hidden z-[60]" data-testid={`${testIdPrefix}-dropdown`}>
          {results.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-[14px] text-[color:var(--gv-ink)]/70 mb-1">{t("common.no_results")}</div>
              <div className="text-[13px] text-[color:var(--gv-ink)]/50">{t("common.try_another")}</div>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              {groups.shop.length > 0 && (
                <Group label={t("searchP.kind.shop")}>
                  {groups.shop.slice(0, 4).map((p) => (
                    <Row key={`shop-${p.slug}`} onClick={() => go(p.url)} Icon={ICON_BY_KIND.shop} image={p.image_url} title={highlight(p.title, q)} subtitle={p.excerpt} testId={`${testIdPrefix}-shop-${p.slug}`} />
                  ))}
                </Group>
              )}
              {groups.academy.length > 0 && (
                <Group label={t("searchP.kind.academy")}>
                  {groups.academy.slice(0, 4).map((c) => (
                    <Row key={`academy-${c.slug}`} onClick={() => go(c.url)} Icon={ICON_BY_KIND.academy} image={c.image_url} title={highlight(c.title, q)} subtitle={c.excerpt} testId={`${testIdPrefix}-academy-${c.slug}`} />
                  ))}
                </Group>
              )}
              {groups.blog.length > 0 && (
                <Group label={t("searchP.kind.blog")}>
                  {groups.blog.slice(0, 3).map((post) => (
                    <Row key={`blog-${post.slug}`} onClick={() => go(post.url)} Icon={ICON_BY_KIND.blog} image={post.image_url} title={highlight(post.title, q)} subtitle={post.excerpt} testId={`${testIdPrefix}-blog-${post.slug}`} />
                  ))}
                </Group>
              )}
              <button
                onClick={submit}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left text-[13px] text-[color:var(--gv-pink)] hover:bg-[color:var(--gv-mist)] border-t border-[color:var(--gv-line)]"
                data-testid={`${testIdPrefix}-see-all`}
              >
                <span>{t("common.see_all")} «&nbsp;<span className="font-medium">{q}</span>&nbsp;»</span>
                <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Group = ({ label, children }) => (
  <div>
    <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.24em] text-[color:var(--gv-ink)]/45">{label}</div>
    <div>{children}</div>
  </div>
);

const Row = ({ onClick, Icon, title, subtitle, image, testId }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-[color:var(--gv-mist)] text-left" data-testid={testId}>
    {image ? (
      <img src={image} alt="" className="w-9 h-11 object-cover rounded-[3px] border border-[color:var(--gv-line)]" />
    ) : (
      <div className="w-9 h-9 rounded-[5px] bg-[color:var(--gv-mist)] flex items-center justify-center">
        <Icon size={15} className="text-[color:var(--gv-blue)]" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-[13.5px] text-[color:var(--gv-ink)] truncate">{title}</div>
      {subtitle && <div className="text-[12px] text-[color:var(--gv-ink)]/55 truncate">{subtitle}</div>}
    </div>
  </button>
);

export default SearchBar;
